import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, AlertTriangle, Truck, Check, X, FileSignature, Send, Package, Clipboard, ShieldCheck, Eye } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useExpeditions, useCreateExpedition, usePreparerExpedition, useExpedierExpedition,
  useLivrerExpedition, useRefuserExpedition, useDeleteExpedition, useViserExpedition, useSignaturesExpedition, useRevoquerLienClient,
} from '@/hooks/useBetonSprint3';
import { operationsApi } from '@/api/operations';
import { useGenererFactureBL } from '@/hooks/useBetonSprint4';
import { FileText } from 'lucide-react';
import { useParc } from '@/hooks/useBetonSprint2';
import { useCommandes } from '@/hooks/useBetonCommandes';
import {
  STATUT_EXPEDITION_LABELS, STATUT_EXPEDITION_COLORS, TYPE_TRANSPORT_LABELS,
  type StatutExpedition, type TypeTransportExpedition, type Expedition,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander, informer } from '@/components/ui/Fenetres';
import { arrondi2, repartirFifo, retirerExcedent } from '@/utils/repartition';

const fmtDT = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const ExpeditionsPage = () => {
  const [statut, setStatut]         = useState<StatutExpedition | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [livrerFor, setLivrerFor]   = useState<Expedition | null>(null);
  // BL-TERRAIN : BL émis et signés sur le portail terrain, à viser par le DAF avant facturation.
  const [aViser, setAViser]         = useState(false);
  const [voirFor, setVoirFor]       = useState<Expedition | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useExpeditions({ statut: statut || undefined, aViser: aViser || undefined, limit: 200 });
  const preparer = usePreparerExpedition();
  const expedier = useExpedierExpedition();
  const refuser  = useRefuserExpedition();
  const remove   = useDeleteExpedition();
  const viser    = useViserExpedition();
  const revoquerLien = useRevoquerLienClient();
  const genererFact = useGenererFactureBL();
  const exps = data?.data ?? [];

  const kpis = useMemo(() => ({
    brouillon: exps.filter((e) => e.statut === 'brouillon').length,
    preparee:  exps.filter((e) => e.statut === 'preparee').length,
    expediee:  exps.filter((e) => e.statut === 'expediee').length,
    livree:    exps.filter((e) => e.statut === 'livree').length,
    poidsTotal: exps.filter((e) => ['expediee', 'livree'].includes(e.statut))
                    .reduce((s, e) => s + parseFloat(e.poidsTotalKg || '0'), 0),
  }), [exps]);

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Expéditions / BL"
        subtitle="Module 8 — Bons de livraison préfa avec lots tracés + signature client"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouveau BL
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Brouillons</div>
            <div className="ds-ks-value">{kpis.brouillon}</div>
            <div className="ds-ks-sub">En préparation</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Préparées</div>
            <div className="ds-ks-value">{kpis.preparee}</div>
            <div className="ds-ks-sub">Prêtes au départ</div>
          </div>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-label">Expédiées</div>
            <div className="ds-ks-value">{kpis.expediee}</div>
            <div className="ds-ks-sub">En route chantier</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Livrées</div>
            <div className="ds-ks-value">{kpis.livree}</div>
            <div className="ds-ks-sub">{formatNumber(kpis.poidsTotal)} kg total</div>
          </div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutExpedition | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_EXPEDITION_LABELS) as StatutExpedition[]).map((s) => (
              <option key={s} value={s}>{STATUT_EXPEDITION_LABELS[s]}</option>
            ))}
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={aViser} onChange={(ev) => setAViser(ev.target.checked)} />
            Émis au terrain, à viser
          </label>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Bons de livraison</div>
              <div className="ds-table-sub">{exps.length} BL · Sprint 3 Module 8</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Référence</th>
                <th>Commande · Chantier</th>
                <th style={{ width: 130 }}>Transport</th>
                <th className="ds-am" style={{ width: 90 }}>Poids (kg)</th>
                <th style={{ width: 80 }}>Camions</th>
                <th style={{ width: 70 }}>Lots</th>
                <th style={{ width: 110 }}>Statut</th>
                <th style={{ width: 110 }}>Signature</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && exps.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <Truck size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune expédition</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={() => setDialogOpen(true)}>
                      <Plus size={12} /> Premier BL
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && exps.map((e) => (
                <tr key={e.id}>
                  <td>
                    <span className="ds-ref-code">{e.reference}</span>
                    {e.source === 'terrain' && <div style={{ fontSize: 10, color: 'var(--tx-3)' }} title={`Émis sur le portail terrain par ${e.emisParNom ?? '—'}`}>📱 terrain</div>}
                  </td>
                  <td>
                    <div className="ds-entry-label">
                      {e.commande?.reference ?? <span style={{ color: 'var(--tx-3)', fontStyle: 'italic' }}>(sortie stock)</span>}
                    </div>
                    <div className="ds-entry-sub">{e.chantier?.nom ?? e.adresseLivraison ?? '—'}</div>
                  </td>
                  <td style={{ fontSize: 11 }}>
                    <div>{TYPE_TRANSPORT_LABELS[e.typeTransport]}</div>
                    <div style={{ color: 'var(--tx-3)' }}>{e.chauffeur ?? e.sousTraitant ?? '—'}</div>
                  </td>
                  <td className="ds-am">{formatNumber(parseFloat(e.poidsTotalKg))}</td>
                  <td style={{ fontSize: 11 }}>{e.nbCamionsEstime}</td>
                  <td style={{ fontSize: 11 }}>{e._count?.lignes ?? e.lignes?.length ?? 0}</td>
                  <td>
                    <span className="ds-bd" style={{ background: STATUT_EXPEDITION_COLORS[e.statut], color: 'white', fontSize: 10 }}>
                      {STATUT_EXPEDITION_LABELS[e.statut]}
                    </span>
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {e.signatureClientAt
                      ? <div style={{ color: 'var(--success)' }}>✓ {e.signatureClientNom}<br /><span style={{ fontSize: 10, color: 'var(--tx-3)' }}>{fmtDT(e.signatureClientAt)}</span>
                          {e.source === 'terrain' && (
                            <button className="ds-btn ds-btn-g ds-btn-sm" style={{ marginTop: 4 }} onClick={() => setVoirFor(e)} title="Voir les signatures dessinées">
                              <Eye size={11} /> Voir
                            </button>
                          )}
                        </div>
                      : <span style={{ color: 'var(--tx-3)' }}>—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {e.statut === 'brouillon' && (
                        <>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => preparer.mutateAsync(e.id), `Préparer ${e.reference} ? Lignes verrouillées.`)} title="Préparer">
                            <Clipboard size={11} />
                          </button>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => remove.mutateAsync(e.id), `Supprimer ${e.reference} ?`)} title="Supprimer" style={{ color: 'var(--danger)' }}>
                            <X size={11} />
                          </button>
                        </>
                      )}
                      {e.statut === 'preparee' && (
                        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => handle(() => expedier.mutateAsync(e.id), `Expédier ${e.reference} ?`)} title="Expédier">
                          <Send size={11} /> Expédier
                        </button>
                      )}
                      {e.statut === 'expediee' && (
                        <>
                          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setLivrerFor(e)} title="Livrer (signature client)">
                            <FileSignature size={11} /> Livrer
                          </button>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={async () => {
                            const r = await demander.texte({ titre: 'Raison du refus livraison ?', confirmer: 'Refuser', danger: true });
                            if (!r) return;
                            await handle(() => refuser.mutateAsync({ id: e.id, payload: { raisonRefus: r } }));
                          }} title="Refus chantier" style={{ color: 'var(--danger)' }}>
                            <X size={11} />
                          </button>
                        </>
                      )}
                      {e.source === 'terrain' && e.statut === 'expediee' && (
                        <>
                          <span style={{ fontSize: 10, color: 'var(--warning, #B45309)' }} title="Le client n'a pas encore signé : il a reçu un lien, ou le chef doit lui en envoyer un">⏳ attend la signature du client</span>
                          <button className="ds-btn ds-btn-g ds-btn-sm" title="Annuler le lien envoyé au client (le chef pourra en générer un nouveau)" disabled={revoquerLien.isPending}
                            onClick={() => handle(() => revoquerLien.mutateAsync(e.id), `Révoquer le lien client de ${e.reference} ? Le client ne pourra plus signer avec ce lien.`)}>
                            <X size={11} /> Révoquer le lien
                          </button>
                        </>
                      )}
                      {e.source === 'terrain' && e.statut === 'livree' && (
                        <button className="ds-btn ds-btn-g ds-btn-sm" title="Télécharger le bon de livraison signé (PDF)" onClick={() => handle(async () => {
                          const blob = await operationsApi.beton.pdfExpedition(e.id);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = `${e.reference}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove();
                          setTimeout(() => URL.revokeObjectURL(url), 10_000);
                        })}>
                          <FileText size={11} /> PDF
                        </button>
                      )}
                      {e.statut === 'livree' && e.source === 'terrain' && !e.viseAt && (
                        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={async () => {
                          const note = await demander.texte({ titre: `Viser ${e.reference} ?`, message: 'Vous confirmez que les lots sortis correspondent à ce qui a été livré. Remarque facultative :', confirmer: 'Viser', min: 0 });
                          if (note === null || note === undefined) return;
                          await handle(() => viser.mutateAsync({ id: e.id, note: note || undefined }));
                        }} title="Viser (obligatoire avant facturation)" disabled={viser.isPending}>
                          <ShieldCheck size={11} /> Viser
                        </button>
                      )}
                      {e.statut === 'livree' && e.source === 'terrain' && e.viseAt && (
                        <span style={{ fontSize: 10, color: 'var(--success)' }} title={e.viseNote ?? undefined}>✓ visé {e.viseParNom ?? ''}</span>
                      )}
                      {e.statut === 'livree' && !(e.source === 'terrain' && !e.viseAt) && (
                        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={async () => {
                          if (!(await demander.confirmer({ titre: `Générer la facture brouillon pour ${e.reference} ?`, message: 'TVA 18% + délai 30j auto.', confirmer: 'Générer' }))) return;
                          try {
                            const inv = await genererFact.mutateAsync({ expeditionId: e.id });
                            informer(`Facture ${inv.reference} créée (statut brouillon)`, 'Voir Finance → Factures pour valider.');
                          } catch (err) { setActionError(extractError(err).message); }
                        }} title="Générer facture brouillon" disabled={genererFact.isPending}>
                          <FileText size={11} /> Facturer
                        </button>
                      )}
                      {e.statut === 'refusee' && (
                        <span style={{ fontSize: 10, color: 'var(--tx-3)' }}>
                          {e.raisonRefus ? `Refus: ${e.raisonRefus.slice(0, 30)}` : '—'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ExpeditionFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <LivrerDialog        expedition={livrerFor} onClose={() => setLivrerFor(null)} />
      <SignaturesDialog    expedition={voirFor}   onClose={() => setVoirFor(null)} />
    </>
  );
};

// ── Dialog création expedition ──────────────────────────

const ExpeditionFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [commandeId, setCommandeId]       = useState('');
  const [adresseLiv, setAdresseLiv]       = useState('');
  const [typeTransport, setTypeT]         = useState<TypeTransportExpedition>('plateau');
  const [chauffeur, setChauffeur]         = useState('');
  const [telChauffeur, setTel]            = useState('');
  const [immat, setImmat]                 = useState('');
  const [capacite, setCapacite]           = useState('25000');
  const [lignes, setLignes]               = useState<Array<{ lotId: string; quantite: number }>>([]);
  // QUANTITÉS : quantité voulue pour un produit, répartie sur les lots les plus anciens (une partie de lot est permise : le lot est coupé à la livraison)
  const [produitCible, setProduitCible]   = useState('');
  const [voulue, setVoulue]               = useState('');
  const [infoRepartition, setInfoRepartition] = useState<string | null>(null);
  const [error, setError]                 = useState<string | null>(null);

  const { data: cmdsData } = useCommandes({ statut: 'confirmee', limit: 100 });
  const { data: parcData } = useParc({ inclureSorties: false, limit: 200 });
  const create = useCreateExpedition();
  const cmds = cmdsData?.data ?? [];
  const lotsParc = parcData?.data ?? [];

  useEffect(() => {
    if (open) {
      setCommandeId(''); setAdresseLiv(''); setTypeT('plateau');
      setChauffeur(''); setTel(''); setImmat(''); setCapacite('25000');
      setLignes([]); setError(null); setProduitCible(''); setVoulue(''); setInfoRepartition(null);
    }
  }, [open]);

  // Filtre lots compatibles avec la commande sélectionnée
  const lotsDispo = useMemo(() => {
    return lotsParc.filter((lot: any) => {
      // Pas réservé ailleurs OU réservé sur la commande courante
      if (lot.commandeReserveeId && commandeId && lot.commandeReserveeId !== commandeId) return false;
      // Pas déjà dans nos lignes
      if (lignes.find((l) => l.lotId === lot.id)) return false;
      return true;
    });
  }, [lotsParc, commandeId, lignes]);

  // Lots proposables : validés au contrôle qualité, pas réservés pour un autre client
  const lotsProposables = useMemo(() => lotsParc.filter((lot: any) =>
    lot.statut === 'valide' && !(lot.commandeReserveeId && commandeId && lot.commandeReserveeId !== commandeId)), [lotsParc, commandeId]);
  const produitsDispo = useMemo(() => {
    const m = new Map<string, { id: string; code: string; designation: string; total: number }>();
    for (const lot of lotsProposables as any[]) {
      const p = lot.ordre?.produit;
      if (!p) continue;
      const cur = m.get(p.id) ?? { id: p.id, code: p.code, designation: p.designation, total: 0 };
      cur.total = arrondi2(cur.total + parseFloat(lot.quantite));
      m.set(p.id, cur);
    }
    return [...m.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [lotsProposables]);
  const objectif = produitCible && parseFloat(voulue) > 0 ? parseFloat(voulue) : null;
  const lotsDuProduit = (lotsParc as any[]).filter((l) => l.ordre?.produit?.id === produitCible);
  const totalProduit = arrondi2(lignes.reduce((n, ln) => n + (lotsDuProduit.some((l) => l.id === ln.lotId) ? (Number.isFinite(ln.quantite) ? ln.quantite : 0) : 0), 0));
  const ecart = objectif === null ? 0 : arrondi2(totalProduit - objectif);

  const proposer = () => {
    if (!objectif) return;
    const candidats = (lotsProposables as any[])
      .filter((l) => l.ordre?.produit?.id === produitCible)
      .map((l) => ({ id: l.id, lotNumber: l.lotNumber, quantite: parseFloat(l.quantite), dateProduction: l.dateProduction }));
    const { lignes: propo, manque } = repartirFifo(candidats, objectif);
    setLignes((prev) => [...prev.filter((ln) => !candidats.some((c) => c.id === ln.lotId)), ...propo.map((p) => ({ lotId: p.lotId, quantite: p.quantite }))]);
    setInfoRepartition(manque > 0 ? `Il n'y en a que ${formatNumber(arrondi2(objectif - manque))} au parc sur ${formatNumber(objectif)} : il en manque ${formatNumber(manque)}.` : null);
  };
  const retirerExces = () => {
    if (!objectif) return;
    const choix = lignes.filter((ln) => lotsDuProduit.some((l) => l.id === ln.lotId) && Number.isFinite(ln.quantite)).map((ln) => {
      const lot = lotsDuProduit.find((l) => l.id === ln.lotId);
      return { id: ln.lotId, lotNumber: lot.lotNumber, quantite: parseFloat(lot.quantite), dateProduction: lot.dateProduction, prise: ln.quantite };
    });
    const gardes = new Map(retirerExcedent(choix, objectif).map((x) => [x.lotId, x.quantite]));
    setLignes((prev) => prev.flatMap((ln) => {
      if (!lotsDuProduit.some((l) => l.id === ln.lotId)) return [ln];
      return gardes.has(ln.lotId) ? [{ ...ln, quantite: gardes.get(ln.lotId)! }] : [];
    }));
  };

  const addLigne = (lotId: string) => {
    const lot: any = lotsParc.find((l: any) => l.id === lotId);
    if (lot) setLignes((prev) => [...prev, { lotId, quantite: parseFloat(lot.quantite) }]);
  };
  const updateQty = (lotId: string, q: number) => setLignes((prev) => prev.map((l) => l.lotId === lotId ? { ...l, quantite: q } : l));
  const removeLigne = (lotId: string) => setLignes((prev) => prev.filter((l) => l.lotId !== lotId));

  const poidsTotal = useMemo(() => {
    return lignes.reduce((s, ln) => {
      const lot: any = lotsParc.find((l: any) => l.id === ln.lotId);
      const poidsKg = lot?.ordre?.produit?.poidsKg ? parseFloat(lot.ordre.produit.poidsKg) : 0;
      return s + poidsKg * ln.quantite;
    }, 0);
  }, [lignes, lotsParc]);

  const nbCamions = Math.max(1, Math.ceil(poidsTotal / (parseFloat(capacite) || 25000)));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (lignes.length === 0) { setError('Sélectionner au moins 1 lot'); return; }
    if (objectif !== null && ecart > 0) { setError(`Le total du produit choisi dépasse de ${formatNumber(ecart)} la quantité voulue : ajustez les quantités.`); return; }
    if (lignes.some((ln) => !(ln.quantite > 0))) { setError('Chaque lot doit avoir une quantité supérieure à zéro.'); return; }
    try {
      await create.mutateAsync({
        commandeId:       commandeId || undefined,
        adresseLivraison: adresseLiv.trim() || undefined,
        typeTransport,
        chauffeur:        chauffeur.trim() || undefined,
        telChauffeur:     telChauffeur.trim() || undefined,
        immatriculations: immat.trim() || undefined,
        capaciteCamionKg: parseFloat(capacite) || 25000,
        lignes,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Nouveau bon de livraison" subtitle="Réf auto BL-YYYY-NNNN · statut initial brouillon" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Commande source (optionnel)">
            <select value={commandeId} onChange={(e) => setCommandeId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Sortie sur stock —</option>
              {cmds.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.reference} · {c.client?.companyName ?? c.client?.fullName ?? '—'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Type transport">
            <select value={typeTransport} onChange={(e) => setTypeT(e.target.value as TypeTransportExpedition)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(TYPE_TRANSPORT_LABELS) as TypeTransportExpedition[]).map((t) => (
                <option key={t} value={t}>{TYPE_TRANSPORT_LABELS[t]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Adresse livraison (si pas dans la commande)">
          <input value={adresseLiv} onChange={(e) => setAdresseLiv(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Chantier XYZ, Cocody…" />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Chauffeur">
            <input value={chauffeur} onChange={(e) => setChauffeur(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
          <Field label="Tel chauffeur">
            <input value={telChauffeur} onChange={(e) => setTel(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="+225..." />
          </Field>
          <Field label="Immat (csv)">
            <input value={immat} onChange={(e) => setImmat(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="TR-456,TR-789" />
          </Field>
        </div>

        <Field label="Capacité camion (kg)">
          <input type="number" value={capacite} onChange={(e) => setCapacite(e.target.value)} className="ds-fi" style={{ width: 200 }} />
        </Field>

        <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-2">
          <div className="text-xs font-semibold text-blue-900 flex items-center gap-1">
            <Package size={11} /> Lots à embarquer ({lignes.length})
          </div>
          <div className="rounded-md border border-blue-200 bg-white p-2 space-y-2">
            <div className="text-xs font-semibold text-blue-900">Quantité à sortir (répartie sur les lots les plus anciens ; une partie de lot est permise)</div>
            <div className="grid grid-cols-3 gap-2">
              <select className="ds-fi" value={produitCible} onChange={(e) => { setProduitCible(e.target.value); setInfoRepartition(null); }} style={{ width: '100%' }}>
                <option value="">— Produit —</option>
                {produitsDispo.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.designation} ({formatNumber(p.total)} au parc)</option>)}
              </select>
              <input type="number" min="0.01" step="0.01" className="ds-fi" placeholder="Quantité (ex. 40)" value={voulue} onChange={(e) => { setVoulue(e.target.value); setInfoRepartition(null); }} />
              <button type="button" className="ds-btn ds-btn-p ds-btn-sm" disabled={!objectif} onClick={proposer}>Proposer les lots</button>
            </div>
            {infoRepartition && <div className="text-xs text-amber-700">{infoRepartition}</div>}
            {objectif !== null && (
              <div className={`text-xs ${ecart > 0 ? 'text-red-700' : ecart === 0 ? 'text-emerald-700' : 'text-blue-900'}`}>
                Choisi : <strong>{formatNumber(totalProduit)}</strong> sur {formatNumber(objectif)}
                {ecart === 0 && ' — objectif atteint'}
                {ecart < 0 && ` — il en manque ${formatNumber(-ecart)} (vous pouvez continuer avec moins)`}
                {ecart > 0 && <> — <strong>{formatNumber(ecart)} de trop</strong> <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={retirerExces} style={{ marginLeft: 6 }}>Retirer l'excédent</button></>}
              </div>
            )}
          </div>
          <div>
            <select className="ds-fi" defaultValue="" onChange={(e) => { if (e.target.value) { addLigne(e.target.value); e.target.value = ''; } }} style={{ width: '100%' }}>
              <option value="">+ Ajouter un lot du parc…</option>
              {lotsDispo.map((lot: any) => (
                <option key={lot.id} value={lot.id}>
                  {lot.lotNumber} · {lot.ordre?.produit?.designation ?? '—'} · {formatNumber(parseFloat(lot.quantite))} {lot.ordre?.produit?.uniteVente ?? ''} · Zone {lot.zoneParc ?? '?'}
                  {lot.commandeReservee ? ` · réservé ${lot.commandeReservee.reference}` : ''}
                </option>
              ))}
            </select>
          </div>
          {lignes.length > 0 && (
            <table className="ds-tbl" style={{ marginTop: 4 }}>
              <thead>
                <tr><th>Lot</th><th className="ds-am" style={{ width: 100 }}>Qté</th><th style={{ width: 60 }}></th></tr>
              </thead>
              <tbody>
                {lignes.map((ln) => {
                  const lot: any = lotsParc.find((l: any) => l.id === ln.lotId);
                  return (
                    <tr key={ln.lotId}>
                      <td style={{ fontSize: 11 }}>
                        <span className="ds-ref-code">{lot?.lotNumber}</span>
                        <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{lot?.ordre?.produit?.designation}</div>
                      </td>
                      <td>
                        <input type="number" min="0.01" step="0.01" max={lot ? parseFloat(lot.quantite) : 9999} value={Number.isFinite(ln.quantite) ? ln.quantite : ''}
                          onChange={(e) => updateQty(ln.lotId, parseFloat(e.target.value))}
                          className="ds-fi" style={{ width: '100%' }} />
                        {lot && ln.quantite > 0 && ln.quantite < parseFloat(lot.quantite) - 1e-9 && (
                          <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>reste {formatNumber(arrondi2(parseFloat(lot.quantite) - ln.quantite))} au parc (lot coupé à la livraison)</div>
                        )}
                      </td>
                      <td>
                        <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={() => removeLigne(ln.lotId)} style={{ color: 'var(--danger)' }}>
                          <X size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {poidsTotal > 0 && (
            <div className="text-xs text-blue-900 font-mono">
              Poids total : <strong>{formatNumber(poidsTotal)} kg</strong> · Capacité : {formatNumber(parseFloat(capacite))} kg/camion · <strong>{nbCamions} camion{nbCamions > 1 ? 's' : ''}</strong>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={lignes.length === 0 || create.isPending || (objectif !== null && ecart > 0)}>
            <Plus size={14} /> {create.isPending ? 'Création…' : 'Créer BL'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── BL-TERRAIN : signatures dessinées sur le portail terrain ────────────
// Les images sont dans un espace privé : le serveur donne des adresses valables 10 minutes, relues à chaque ouverture.

const SignaturesDialog = ({ expedition, onClose }: { expedition: Expedition | null; onClose: () => void }) => {
  const { data, isLoading, isError } = useSignaturesExpedition(expedition?.id);
  if (!expedition) return null;
  const bloc = (titre: string, nom: string | null | undefined, at: string | null | undefined, url: string | null | undefined) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{titre} — {nom ?? '—'}{at ? ` · ${fmtDT(at)}` : ''}</div>
      {url
        ? <img src={url} alt={`Signature : ${titre}`} style={{ marginTop: 6, maxWidth: '100%', border: '1px solid var(--bd, #ddd)', borderRadius: 6, background: '#fff' }} />
        : <div style={{ marginTop: 6, fontSize: 12, color: 'var(--tx-3)' }}>{isLoading ? 'Chargement…' : 'Image indisponible.'}</div>}
    </div>
  );
  return (
    <Dialog open onClose={onClose} title={`Signatures — ${expedition.reference}`} subtitle={`Émis sur le terrain par ${expedition.emisParNom ?? '—'}`} size="md">
      {isError && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">Impossible de charger les signatures.</div>}
      {bloc('Client', expedition.signatureClientNom, expedition.signatureClientAt, data?.client)}
      {expedition.signatureClientMode && (
        <div style={{ fontSize: 12, color: 'var(--tx-3)', margin: '-8px 0 12px' }}>
          {expedition.signatureClientMode === 'lien' ? 'Signé par le client sur son propre téléphone, par lien.' : 'Signé sur le téléphone du chef de chantier.'}
        </div>
      )}
      {expedition.signatureClientObservations && (
        <div style={{ marginBottom: 14, padding: 10, borderRadius: 6, background: 'var(--bg-2, #fff7ed)', fontSize: 13 }}>
          <b>Observations du client :</b> {expedition.signatureClientObservations}
        </div>
      )}
      {bloc('Chauffeur', expedition.signatureChauffeurNom, expedition.signatureChauffeurAt, data?.chauffeur)}
      {data?.photoBon && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Photo du bon papier</div>
          <img src={data.photoBon} alt="Photo du bon papier" style={{ marginTop: 6, maxWidth: '100%', borderRadius: 6 }} />
        </div>
      )}
      <div className="flex justify-end border-t pt-3"><button className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button></div>
    </Dialog>
  );
};

// ── Dialog livrer (signature) ───────────────────────────

const LivrerDialog = ({ expedition, onClose }: { expedition: Expedition | null; onClose: () => void }) => {
  const open = expedition !== null;
  const livrer = useLivrerExpedition();
  const [nom, setNom]     = useState('');
  const [hash, setHash]   = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setNom(''); setHash(''); setNotes(''); setError(null); } }, [open]);

  if (!expedition) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await livrer.mutateAsync({ id: expedition.id, payload: { signataireNom: nom.trim(), signatureHash: hash.trim() || undefined, notes: notes.trim() || undefined } });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Livraison signée — ${expedition.reference}`} subtitle="Saisie nom du signataire client + signature électronique (optionnelle)" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Nom signataire client *">
          <input value={nom} onChange={(e) => setNom(e.target.value)} required placeholder="M. Kouassi BTP" className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <Field label="Signature (hash ou URL upload — optionnel)">
          <input value={hash} onChange={(e) => setHash(e.target.value)} placeholder="data:image/png... ou hash" className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <Field label="Notes livraison">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Observations chantier..." />
        </Field>

        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
          ⚠ Cette action verrouille le BL (livré) + marque les lots sortis du parc + crée mouvements parc audit.
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!nom.trim() || livrer.isPending}>
            <Check size={14} /> {livrer.isPending ? 'Enregistrement…' : 'Confirmer livraison signée'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
